import React, { useCallback, useState, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { Record } from 'immutable';
import { useDispatch } from 'react-redux';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Utils
import * as MeetingItemUtils from 'Shared/utils/MeetingItem.utils';
import { bodyWithoutDescription } from 'Shared/components/DraftEditor/utils';
import { createRichTextEditorState } from 'Wpds/components/rich-text/utils/EditorState.utils';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';
import { editorStateToJsonString, editorStateForEditorComponent } from 'Shared/utils/draftJsHelpers';

// Actions
import { updateItemBodyInRedux } from 'Shared/actions/MeetingItems.actions';

// Components
import { modifyUrlBySmartLink } from 'Shared/components/DraftEditor/plugins/smartLinksPlugin';
import DraftEditor from 'Shared/components/DraftEditor/DraftEditor';

import '@workpatterns/draft-js-mention-plugin/lib/plugin.css';
import 'draft-js/dist/Draft.css';

type Props = {
  meetingItem: Record<MeetingItem>;
  isSelected?: boolean;
  handleUpdateBody?: Function;
  readonly?: boolean;
  setSelectedActionItemUuid?: Function;
  handleNestOnTab?: Function;
  handleCreateTempItem?: Function;
  handleDeleteItem?: Function;
  handleRemoveNesting?: Function;
  onClickCallback?: Function;
  hideDescription?: boolean;
};

const MeetingItemInput = ({
  meetingItem,
  handleUpdateBody,
  isSelected = false,
  setSelectedActionItemUuid,
  readonly = false,
  handleNestOnTab,
  handleCreateTempItem,
  handleDeleteItem,
  handleRemoveNesting,
  onClickCallback,
  hideDescription = false,
}: Props) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const slug = MeetingItemHelpers.getUuid(meetingItem);
  const body = MeetingItemHelpers.getBody(meetingItem);
  const itemType = MeetingItemHelpers.getItemType(meetingItem);

  const isActionItem = MeetingItemUtils.isActionItem(itemType);

  const placeholder = isActionItem ? 'Add an item' : 'Add a topic';

  const [editorState, setEditorState] = useState(
    hideDescription ? createRichTextEditorState(bodyWithoutDescription(body)) : editorStateForEditorComponent(body)
  );
  const [backspacePressCount, setBackspacePressCount] = useState(0);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setTimeout(() => {
        setEditorState(
          hideDescription
            ? createRichTextEditorState(bodyWithoutDescription(body))
            : editorStateForEditorComponent(body)
        );
      }, 0);
    }
  }, [body, isActive, hideDescription]);

  const handleEditorContainerClick = () => {
    inputRef.current.focus();
    setIsActive(true);
    if (setSelectedActionItemUuid) setSelectedActionItemUuid(slug);
    if (onClickCallback) onClickCallback();
  };

  const debounceUpdate = useRef(
    debounce(
      newBody => {
        if (handleUpdateBody) handleUpdateBody(newBody);
      },
      500,
      { leading: false, trailing: true }
    )
  );

  const handleUpdate = newBody => {
    setEditorState(newBody);
    if (!isActive) return false;
    const newEditorStateJson = editorStateToJsonString(newBody);
    dispatch(updateItemBodyInRedux(slug, newEditorStateJson));
    debounceUpdate.current(newEditorStateJson);
  };

  const setState = useCallback(
    state => {
      if (typeof state === 'function') {
        handleUpdate(state({ editorState }).editorState);
      } else {
        handleUpdate(state.editorState);
      }
    },
    [editorState]
  );

  const handleEditorChange = newEditorState => {
    modifyUrlBySmartLink(newEditorState, { setState });
  };

  const handleEditorContainerKeyPress = evt => {
    const parentId = MeetingItemHelpers.getParentUuid(meetingItem);
    const isSubItem = MeetingItemUtils.isSubItem(parentId);
    if (evt.key === 'Tab' && handleNestOnTab && handleRemoveNesting) {
      evt.preventDefault();
      if (evt.shiftKey && isSubItem) {
        handleRemoveNesting(meetingItem);
      } else if (!evt.shiftKey && !isSubItem) {
        handleNestOnTab(meetingItem);
      }
    } else if (evt.key === 'Backspace' && handleDeleteItem && !editorState.getCurrentContent().hasText()) {
      evt.preventDefault();
      if (backspacePressCount === 1) {
        handleDeleteItem(meetingItem);
      } else {
        setBackspacePressCount(backspacePressCount + 1);
      }
    }
    // } else if (evt.key === 'ArrowUp' && evt.keyCode === 38) onArrowUpClick({ uuid });
    // else if (evt.key === 'ArrowDown' && evt.keyCode === 40) onArrowDownClick({ uuid });
  };

  const handleKeyCommandActionsMap = useCallback(
    () => ({
      'save-block': () => {
        const parentId = MeetingItemHelpers.getParentUuid(meetingItem);
        const isSubItem = MeetingItemUtils.isSubItem(parentId);
        const createdAt = MeetingItemHelpers.getCreatedAt(meetingItem);
        if (!createdAt && !editorState.getCurrentContent().hasText()) return;

        if (isSubItem) {
          handleCreateTempItem(meetingItem, 'create_sub_from_sub');
        } else {
          handleCreateTempItem(meetingItem, 'create_parent_from_parent');
        }
      },
    }),
    [handleCreateTempItem, meetingItem, editorState]
  );

  useEffect(() => {
    if (isSelected && setSelectedActionItemUuid) {
      handleEditorContainerClick();
    }
  }, [isSelected]);

  return (
    <div onClick={handleEditorContainerClick} onKeyDown={handleEditorContainerKeyPress}>
      <DraftEditor
        meetingItemUuid={slug}
        editorState={editorState}
        placeholder={placeholder}
        onChange={handleEditorChange}
        editorRef={inputRef}
        onKeyCommandActionsMap={handleKeyCommandActionsMap}
        readOnly={readonly}
      />
    </div>
  );
};

export default MeetingItemInput;
